import * as React from 'react'
import * as classNames from 'classnames'
import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { Input, Select, Textarea } from '@mobiscroll/react-latest'
import States from '../../../../constants/States'
import { sendEmailOptions, timeFormatOptions, timezoneOptions } from 'src/constants/create-organization-form'
import { IOrgObj } from '../../../../types'
import { isNotBlank, isValidEmail } from '../../../../utils/validators'
import UIMessages from '../../../../utils/ui-messages'
import ImageUpload from '../../../ui/ImageUpload'
import ErrorMessage from '../../../ui/ErrorMessage'
import CreateOrganizationBottomBar from '../CreateOrganizationBottomBar'
import PhoneInput from 'react-phone-number-input'
import FloatingForm from 'src/components/ui/form/FloatingForm'
export interface IFormValues {
  formError?: string
  email: string
  sendEmail: number
  logo: string
  name: string
  phone: string
  state: string
  timeZone: number
  callerID: string | undefined
  timeFormat: string
  website: string
  description: string
}

interface ISelectOption {
  label: string
  value: string
}

interface IFormProps {
  isSaved: boolean
  organization: IOrgObj
  orgId: string
  isCompleted: boolean
  isEdit?: boolean
}

interface IRiderFormProps {
  currentStep: number
  isSaved: boolean
  organization: IOrgObj
  orgId: string
  isCompleted: boolean
  isEdit?: boolean
  onCompleteStep: (step: number) => void
  onSaveOrganizationInfo: (organization: IOrgObj) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

export const InnerForm = (props: IFormProps & FormikProps<IFormValues>): any => {
  const {
    touched,
    errors,
    isSaved,
    isSubmitting,
    isCompleted,
    isEdit,
    orgId,
    organization,
    setFieldTouched,
    setFieldValue,
    handleBlur
  } = props

  const inputProps = {
    inputStyle: 'box' as const,
    labelStyle: 'floating' as const,
    theme: 'ios'
  }

  const sendEmailDefaultValue = sendEmailOptions.find(i => i.value === organization.sendEmail)
  const timeFormatDefaultValue = timeFormatOptions.find(i => i.value === organization.timeFormat)
  const timezoneDefaultValue = timezoneOptions.find(i => i.value === organization.timeZone)

  const textInput = (value: FieldProps<IFormValues>) => {
    const { field, form } = value
    const onChangeWrap = (e: React.ChangeEvent<HTMLInputElement>) => {
      field.onChange(e)
      form.setTouched(field.value)
    }

    const inputDetails = {
      email: {
        label: UIMessages.common.email,
        required: false,
        type: 'text'
      },
      name: {
        label: `${UIMessages.common.nameLabel}*`,
        required: true,
        type: 'text'
      },
      phone: {
        label: UIMessages.common.phoneNumber,
        required: false,
        type: 'text'
      },
      website: {
        label: UIMessages.common.website,
        required: false,
        type: 'text'
      }
    }
    const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Input {...field} {...inputProps} {...textInputProps} onChange={onChangeWrap} />
  }

  const textArea = (value: FieldProps<IFormValues>) => {
    const { field, form } = value
    const onChangeWrap = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      field.onChange(e)
      form.setTouched(field.value)
    }

    const inputDetails = {
      description: {
        label: UIMessages.common.description,
        required: false
      }
    }
    const textareaProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Textarea {...field} {...inputProps} {...textareaProps} rows={5} onChange={onChangeWrap} />
  }

  const select = (value: FieldProps<IFormValues>) => {
    const { field } = value
    const onChangeWrap = (value: string | number) => {
      setFieldTouched(field.name)
      setFieldValue(field.name, value)
    }

    const inputDetails = {
      sendEmail: {
        data: sendEmailOptions,
        defaultValue: sendEmailDefaultValue ? sendEmailDefaultValue.value : sendEmailOptions[0].value,
        filter: false,
        label: `${UIMessages.organization.organizationInfo.sendEmail}*`,
        required: true
      },
      timeFormat: {
        data: timeFormatOptions,
        defaultValue: timeFormatDefaultValue ? timeFormatDefaultValue.value : timeFormatOptions[0].value,
        filter: false,
        label: `${UIMessages.organization.organizationInfo.timeFormat}*`,
        required: true
      },
      timeZone: {
        data: timezoneOptions,
        defaultValue: timezoneDefaultValue ? timezoneDefaultValue.value : timezoneOptions[0].value,
        filter: true,
        label: `${UIMessages.organization.organizationInfo.timeZone}*`,
        required: true
      }
    }
    const selectProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Select {...field} {...inputProps} {...selectProps} onChange={({ value }) => onChangeWrap(value)} />
  }

  const getInputTitle = (title: string, isOptional: boolean = false) => {
    return (
      <div className='flex'>
        <span>{title}</span>
        {!isOptional && <span className='font-small font-red'>*</span>}
        <span className='flex1'>&nbsp;</span>
        {isOptional && <span className='font-small font-grey2 lowercase'>{UIMessages.common.optional}</span>}
      </div>
    )
  }

  const handlePhoneField = (value: any) => {
    setFieldTouched('callerID')
    setFieldValue('callerID', value)
  }
  const bodyStyle = isEdit
    ? classNames({ 'pointer-events-auto opacity1': !isSubmitting }, { 'pointer-events-none opacity04': isSubmitting })
    : classNames(
        { 'pointer-events-auto opacity1': !(isSaved || isCompleted || isSubmitting) },
        { 'pointer-events-none opacity04': isSaved || isCompleted || isSubmitting }
      )
  const isTouched = Object.keys(touched).length > 0
  const nextStepPath = isEdit
    ? `/organizations/${orgId}/edit/organization-subscription`
    : '/organizations/create/organization-subscription'

  return (
    <Form className='org-main-info-container'>
      <FloatingForm>
        <div className={`${bodyStyle}`}>
          <ErrorMessage visible={!!errors.formError}>{UIMessages.common.failedOrganizationCreate}</ErrorMessage>
          <div className='mbsc-grid' style={{ padding: '0' }}>
            <div className='mbsc-row'>
              <div className='mbsc-col-lg-8'>
                <p className='font-grey3 bold'>{UIMessages.common.organizationInfo}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  {orgId ? (
                    <div className='mbsc-col-12 mbsc-padding'>
                      <React.Fragment>
                        <p className='bold font-grey3 font-small mb1'>{UIMessages.common.logo}</p>
                        <ImageUpload entityId={orgId} />
                      </React.Fragment>
                    </div>
                  ) : null}
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='name' label='ios' render={textInput} />
                    {touched.name && errors.name && (
                      <span className='ErrorMsgDivForName font-small weight-300 font-red block relative h0'>
                        {errors.name}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='phone' render={textInput} value={organization.phone} />
                    {touched.phone && errors.phone && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.phone}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='email' render={textInput} />
                    {touched.email && errors.email && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.email}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='website' render={textInput} />
                    {touched.website && errors.website && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.website}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='timeZone' render={select} />
                    {touched.timeZone && errors.timeZone && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.timeZone}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='timeFormat' render={select} />
                    {touched.timeFormat && errors.timeFormat && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.timeFormat}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <PhoneInput
                      className={classNames('phone-input', {
                        'border-red :focus-red': !!(touched.phone && errors.phone)
                      })}
                      name='callerID'
                      placeholder='Caller ID'
                      defaultCountry='US'
                      onChangeFormattedText={(text: string) => {
                        handlePhoneField(text)
                      }}
                      value={organization.callerID}
                      onChange={handlePhoneField}
                      onBlur={handleBlur}
                    />
                    {touched.callerID && errors.callerID && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.callerID}
                      </span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-padding'>
                    <Field name='description' render={textArea} />
                    {touched.description && errors.description && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.description}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className='mbsc-col-lg-4'>
                <p className='font-grey3 bold'>{UIMessages.common.organizationSetting}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-padding'>
                    <Field name='sendEmail' render={select} />
                    {touched.sendEmail && errors.sendEmail && (
                      <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                        {errors.sendEmail}
                      </span>
                    )}
                  </div>
                </div>
                <p className='font-grey3 bold'>{UIMessages.common.tips}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-padding'>
                    <div className='font-light-grey'>{UIMessages.common.organizationInfoTips}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FloatingForm>
      <CreateOrganizationBottomBar
        isEdit={isEdit}
        isSaved={!isTouched || (!isEdit && (isSaved || isCompleted))}
        isSaving={isSubmitting}
        nextStepPath={nextStepPath}
      />
    </Form>
  )
}

export const OrganizationInfoForm = withFormik<IRiderFormProps, IFormValues>({
  mapPropsToValues: (props: IRiderFormProps): IFormValues => {
    const {
      email,
      sendEmail,
      logo,
      name,
      phone,
      state,
      timeZone,
      callerID,
      timeFormat,
      website,
      description
    } = props.organization
    return {
      email,
      sendEmail: sendEmail || sendEmailOptions[0].value,
      logo,
      name,
      phone,
      callerID: callerID || '',
      website,
      state: state || States[0].value,
      timeZone: Number(timeZone) || timezoneOptions[0].value,
      timeFormat: timeFormat || timeFormatOptions[0].value,
      description: description || ''
    }
  },
  mapValuesToPayload: x => x,
  validate: ({ name, state, email, timeZone, timeFormat }: IFormValues, { showMessage }): FormikErrors<IFormValues> => {
    const errors: FormikErrors<IFormValues> = {}
    if (!isNotBlank(name)) {
      errors.name = UIMessages.rider.validation.orgNameIsRequired
    }
    if (!isNotBlank(state)) {
      errors.state = UIMessages.rider.validation.stateIsRequired
    }
    if (email && !isValidEmail(email)) {
      errors.email = UIMessages.rider.validation.invalidEmailAddress
    }

    return errors
  },
  handleSubmit: (
    { email, sendEmail, logo, name, phone, state, timeZone, timeFormat, callerID, website, description }: IFormValues,
    { props, setSubmitting }
  ) => {
    setSubmitting(true)
    props.onCompleteStep(1)
    props
      .onSaveOrganizationInfo({
        ...props.organization,
        email,
        sendEmail,
        logo,
        name,
        phone,
        // state is no longer needed for org's info. this will be hardecoded here for now until we remove the code
        // from the backend
        state: 'AL',
        timeFormat,
        timeZone,
        callerID,
        website,
        description
      })
      .then(() => {
        setSubmitting(false)
      })
  }
})(InnerForm)

export default OrganizationInfoForm



// WEBPACK FOOTER //
// ./src/components/organizations/create/organizationinfo/OrganizationInfoForm.tsx